import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { TwoPaneLayout } from '../components/TwoPaneLayout';
import { ApiError, login as apiLogin, resetPassword } from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import PasswordField from '../components/PasswordField';

/**
 * Reset-password page. Email + 6-digit code + new password → POST
 * /auth/reset-password. On success, auto-login with the new password
 * and navigate to /dashboard so the user doesn't have to retype.
 *
 * Email pre-fills from `?email=` (set by ForgotPassword's "I have the
 * code" button), but is editable in case the user lands here from an
 * email client that strips query strings or wants to reset a different
 * account.
 *
 * NOTE: The Gateway flow uses a 6-digit numeric code emailed to the
 * user, NOT a token-in-URL. The reset email itself contains the code;
 * the SPA never sees a token. (We had a stub /reset-password/:token
 * route earlier — that was wrong; the App.tsx route is now plain
 * /reset-password.)
 */
export function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();

  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function validate(): string | null {
    if (!email) return 'Please enter your email.';
    if (!/^\d{6}$/.test(code)) return 'The reset code is 6 digits.';
    if (newPassword.length < 8)
      return 'New password must be at least 8 characters.';
    if (newPassword !== confirmPassword) return "Passwords don't match.";
    return null;
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(email, code, newPassword);
      // Auto-login with the new password so the user lands on the
      // dashboard without retyping. If this fails (rare — same server
      // just accepted it), fall through to /login.
      try {
        await apiLogin(email, newPassword);
        await refresh();
        navigate('/dashboard', { replace: true });
        return;
      } catch {
        navigate('/login?reset=success', { replace: true });
        return;
      }
    } catch (err) {
      if (err instanceof ApiError) {
        // Gateway returns specific codes; surface the message which is
        // already user-facing ("Invalid or expired reset code", etc.).
        setError(err.message);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Password reset failed. Please try again.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <TwoPaneLayout>
      <form onSubmit={onSubmit} noValidate>
        <h2 className="h4 mb-3">Set a new password</h2>
        <p className="text-muted small mb-4">
          Enter the 6-digit code from your reset email along with your new
          password.
        </p>

        <div className="mb-3">
          <label className="form-label small text-muted">Email</label>
          <input
            type="email"
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label small text-muted">Reset code</label>
          <input
            type="text"
            inputMode="numeric"
            className="form-control"
            value={code}
            onChange={(e) =>
              // Strip non-digits so paste-with-spaces works; cap at 6.
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            placeholder="123456"
            autoComplete="one-time-code"
            maxLength={6}
            style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.1rem' }}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label small text-muted">New password</label>
          <PasswordField
            className="form-control"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
          <div className="form-text">At least 8 characters.</div>
        </div>

        <div className="mb-3">
          <label className="form-label small text-muted">Confirm new password</label>
          <PasswordField
            className="form-control"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary w-100 mb-3"
          disabled={
            submitting || !email || code.length !== 6 || !newPassword ||
            !confirmPassword
          }
        >
          {submitting ? 'Resetting…' : 'Set new password'}
        </button>

        <hr className="my-4" />
        <div className="d-flex justify-content-between small">
          <Link to="/forgot-password">Need a new code?</Link>
          <Link to="/login">Back to sign in</Link>
        </div>
      </form>
    </TwoPaneLayout>
  );
}
